'use client';

import type { ComponentType, FC } from 'react';
import { m, useReducedMotion } from 'motion/react';
import { usePathname } from 'next/navigation';

import { Mail, SquarePen } from 'lucide-react';

import { Heading } from '@/components/ui/heading/heading';
import SocialIcons from '@/shared/icons/social-icons';

import type { TApiButton, TApiHeading, TApiText } from '@/shared/apiComponentTypes';
import { fadeSlideUp, scaleIn, staggerContainer } from '@/utils/animation/variants';

type TCTAButton = TApiButton & { iconName: keyof typeof SocialIcons; href: string; openInNewTab?: boolean };

type TCTAButtons = {
  id: number;
  __component: 'components.cta-buttons-list';
  ctaButtons: TCTAButton[];
};

export type TCTASection = [TApiHeading, TApiText, TCTAButtons];

const customIconByLabel: Record<string, ComponentType<{ size?: number }>> = {
  'formularz kontaktowy': SquarePen,
};

const customIconByHref: Record<string, ComponentType<{ size?: number }>> = {
  '/kontakt': SquarePen,
};

const customSecondaryLabels: Record<string, string> = {
  WhatsApp: '+48 575 109 749',
  Facebook: 'petrolboys.import',
  'Formularz kontaktowy': '/kontakt',
};

const formatSecondaryLabel = (href: string, label: string) => {
  if (customSecondaryLabels[label]) {
    return customSecondaryLabels[label];
  }

  if (!href) return '';

  if (href.startsWith('mailto:')) {
    return href.replace('mailto:', '');
  }

  if (href.startsWith('tel:')) {
    const digits = href.replace('tel:', '').replace(/\D/g, '');
    if (!digits) {
      return href.replace('tel:', '');
    }
    return digits.length >= 11 ? digits.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '+$1 $2 $3 $4') : digits;
  }

  if (href.includes('facebook.com')) {
    return 'facebook.com/petrolboys.import';
  }

  if (href.includes('wa.me') || href.includes('whatsapp')) {
    const digits = href.replace(/[^\d]/g, '');
    if (!digits) {
      return 'WhatsApp czat';
    }
    return digits.length >= 11 ? digits.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '+$1 $2 $3 $4') : digits;
  }

  if (href.startsWith('/')) {
    return href;
  }

  return href;
};

const CTAButtonCard = ({ item }: { item: TCTAButton }) => {
  const iconName = item.iconName ?? '';
  const socialIcon = iconName ? (SocialIcons[iconName as keyof typeof SocialIcons] as ComponentType<{ size?: number }>) : null;
  const normalizedLabel = item.label.trim().toLowerCase();
  const Icon = customIconByLabel[normalizedLabel] || customIconByHref[item.href] || socialIcon || Mail;
  const secondaryLabel = formatSecondaryLabel(item.href, item.label);

  return (
    <a
      href={item.href}
      target={item.openInNewTab ? '_blank' : '_self'}
      rel={item.openInNewTab ? 'noreferrer' : undefined}
      className="group flex flex-col gap-4 rounded-3xl border border-neutral-800 bg-backgroundLight/80 px-6 py-5 transition-colors duration-300 hover:border-neutral-700 hover:bg-neutral-900/60"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-neutral-900 text-action transition-colors group-hover:bg-action group-hover:text-neutral-900">
          <Icon size={18} />
        </span>
        <span className="text-xs uppercase tracking-[0.3em] text-neutral-500">{item.label}</span>
      </div>
      <p className="font-oswald text-lg text-white line-clamp-1">{secondaryLabel}</p>
    </a>
  );
};

export const CTASection: FC<TCTASection> = props => {
  const prefersReducedMotion = useReducedMotion();
  const pathname = usePathname();

  return (
    <m.div
      key={pathname}
      initial={prefersReducedMotion ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={staggerContainer}
      className="rounded-[28px] border border-neutral-900/40 bg-neutral-900/30 px-6 py-10 shadow-[0_15px_45px_rgba(0,0,0,0.35)] md:px-12 md:py-12"
    >
      <div className="flex flex-col gap-8 2xl:flex-row 2xl:items-center 2xl:gap-16">
        <m.div variants={fadeSlideUp} className="w-full space-y-5 2xl:max-w-xl">
          {props[0].caption && (
            <div className="flex items-center gap-3 text-xs font-oswald uppercase tracking-[0.35em] text-neutral-400">
              <span className="inline-block h-1.5 w-10 rounded-full bg-action" />
              {props[0].caption}
            </div>
          )}

          <Heading
            variant="h2"
            className="text-4xl leading-tight text-white"
            highlight={props[0].highlight}
          >
            {props[0].title}
          </Heading>

          <p className="text-base leading-relaxed text-neutral-300">{props[1].text}</p>
        </m.div>

        <m.div variants={fadeSlideUp} className="relative w-full 2xl:flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-px bg-gradient-to-b from-transparent via-neutral-800 to-transparent 2xl:block" />
          <div className="pl-0 2xl:pl-10">
            <p className="mb-4 text-xs uppercase tracking-[0.35em] text-neutral-500">Skontaktuj się z nami</p>
            <m.div
              variants={staggerContainer}
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
            >
              {props[2].ctaButtons.map(item => (
                <m.div key={item.label} variants={scaleIn}>
                  <CTAButtonCard item={item} />
                </m.div>
              ))}
            </m.div>
          </div>
        </m.div>
      </div>
    </m.div>
  );
};
